<template>
  <v-dialog v-model="show" max-width="1000px" persistent scrollable>
    <v-card>
      <v-card-title>
        <span class="headline">{{ isEdit ? 'Regel bearbeiten' : 'Neue Regel erstellen' }}</span>
      </v-card-title>

      <v-card-text style="max-height: 70vh;">
        <v-form ref="form" v-model="valid">
          <!-- Tabs für bessere Übersicht -->
          <v-tabs v-model="tab" class="mb-4">
            <v-tab>Grunddaten</v-tab>
            <v-tab>Filter</v-tab>
            <v-tab>Nachricht</v-tab>
            <v-tab>Limits</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <!-- Tab 1: Grunddaten -->
            <v-tab-item>
              <v-text-field
                v-model="form.name"
                label="Regelname *"
                :rules="[v => !!v || 'Regelname ist erforderlich']"
                outlined
                dense
              ></v-text-field>

              <v-select
                v-model="form.priority"
                :items="priorities"
                label="Priorität"
                outlined
                dense
              ></v-select>

              <v-radio-group v-model="form.filterType" label="Filter-Verknüpfung" row>
                <v-radio label="ODER (mindestens eine Bedingung)" value="OR"></v-radio>
                <v-radio label="UND (alle Bedingungen)" value="AND"></v-radio>
              </v-radio-group>

              <v-switch
                v-model="form.active"
                label="Regel aktiv"
                color="primary"
              ></v-switch>
            </v-tab-item>

            <!-- Tab 2: Filter -->
            <v-tab-item>
              <v-alert type="info" dense text class="mb-4">
                <strong>Filter-Verknüpfung "{{ form.filterType }}":</strong>
                {{ form.filterType === 'OR' ? 'Mindestens eine Bedingung muss erfüllt sein' : 'Alle Bedingungen müssen erfüllt sein' }}
              </v-alert>

              <v-autocomplete
                v-model="form.filterSkus"
                :items="filterOptions.skus"
                label="SKUs auswählen"
                multiple
                chips
                deletable-chips
                outlined
                dense
                :loading="loadingFilterOptions"
                hint="SKUs aus der Datenbank auswählen"
                persistent-hint
                class="mb-4"
                clearable
                :search-input.sync="skuSearch"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    small
                    close
                    @click:close="removeFilterItem('filterSkus', item)"
                  >
                    {{ item }}
                  </v-chip>
                  <span
                    v-if="index === 3"
                    class="grey--text text-caption"
                  >
                    (+{{ form.filterSkus.length - 3 }} weitere)
                  </span>
                </template>
                <template v-slot:prepend-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        {{ filterOptions.skus.length }} SKUs verfügbar
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mb-2"></v-divider>
                </template>
              </v-autocomplete>

              <v-autocomplete
                v-model="form.filterCountries"
                :items="filterOptions.countries"
                label="Lieferländer auswählen"
                multiple
                chips
                deletable-chips
                outlined
                dense
                :loading="loadingFilterOptions"
                hint="Länder aus der Liste auswählen"
                persistent-hint
                class="mb-4"
                clearable
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    small
                    close
                    @click:close="removeFilterItem('filterCountries', item)"
                  >
                    <v-icon small left>mdi-flag</v-icon>
                    {{ item }}
                  </v-chip>
                  <span
                    v-if="index === 3"
                    class="grey--text text-caption"
                  >
                    (+{{ form.filterCountries.length - 3 }} weitere)
                  </span>
                </template>
              </v-autocomplete>

              <v-autocomplete
                v-model="form.filterCategories"
                :items="filterOptions.categories"
                label="Kategorien auswählen (aus PIM-Daten)"
                multiple
                chips
                deletable-chips
                outlined
                dense
                :loading="loadingFilterOptions"
                hint="Produktkategorien aus der Datenbank"
                persistent-hint
                class="mb-4"
                clearable
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    small
                    close
                    @click:close="removeFilterItem('filterCategories', item)"
                  >
                    <v-icon small left>mdi-tag</v-icon>
                    {{ item }}
                  </v-chip>
                  <span
                    v-if="index === 3"
                    class="grey--text text-caption"
                  >
                    (+{{ form.filterCategories.length - 3 }} weitere)
                  </span>
                </template>
                <template v-slot:prepend-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        {{ filterOptions.categories.length }} Kategorien verfügbar
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mb-2"></v-divider>
                </template>
              </v-autocomplete>

              <v-autocomplete
                v-model="form.filterProducts"
                :items="filterOptions.products"
                label="Produkte auswählen (aus PIM-Daten)"
                multiple
                chips
                deletable-chips
                outlined
                dense
                :loading="loadingFilterOptions"
                hint="Produktnamen aus der Datenbank"
                persistent-hint
                clearable
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    small
                    close
                    @click:close="removeFilterItem('filterProducts', item)"
                  >
                    <v-icon small left>mdi-package-variant</v-icon>
                    {{ item }}
                  </v-chip>
                  <span
                    v-if="index === 3"
                    class="grey--text text-caption"
                  >
                    (+{{ form.filterProducts.length - 3 }} weitere)
                  </span>
                </template>
                <template v-slot:prepend-item>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        {{ filterOptions.products.length }} Produkte verfügbar
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mb-2"></v-divider>
                </template>
              </v-autocomplete>
            </v-tab-item>

            <!-- Tab 3: Nachricht -->
            <v-tab-item>
              <v-text-field
                v-model="form.messageTitle"
                label="Nachrichtentitel *"
                :rules="[v => !!v || 'Titel ist erforderlich']"
                outlined
                dense
                class="mb-4"
              ></v-text-field>

              <!-- Quill HTML Editor -->
              <div class="mb-4">
                <label class="v-label theme--light mb-2">HTML Nachricht *</label>
                <quill-editor
                  v-model="form.messageContent"
                  :options="editorOptions"
                  @ready="onEditorReady"
                  style="min-height: 200px;"
                />
              </div>

              <!-- Bild Upload -->
              <v-file-input
                v-model="imageFile"
                label="Bild hochladen und einfügen"
                accept="image/*"
                outlined
                dense
                @change="uploadImage"
                :loading="uploadingImage"
                class="mb-4"
              ></v-file-input>

              <!-- Farben -->
              <v-row>
                <v-col cols="6">
                  <v-text-field
                    v-model="form.messageBgColor"
                    label="Hintergrundfarbe (HEX)"
                    outlined
                    dense
                    prepend-icon="mdi-palette"
                  >
                    <template v-slot:append>
                      <input
                        type="color"
                        v-model="form.messageBgColor"
                        style="width: 40px; height: 30px; border: none; cursor: pointer;"
                      />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="form.messageTextColor"
                    label="Textfarbe (HEX)"
                    outlined
                    dense
                    prepend-icon="mdi-format-color-text"
                  >
                    <template v-slot:append>
                      <input
                        type="color"
                        v-model="form.messageTextColor"
                        style="width: 40px; height: 30px; border: none; cursor: pointer;"
                      />
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- Tab 4: Limits -->
            <v-tab-item>
              <v-text-field
                v-model.number="form.maxPerDay"
                label="Max. Anzeigen pro Tag und Nutzer"
                type="number"
                min="1"
                outlined
                dense
                hint="Wie oft darf diese Nachricht einem Nutzer pro Tag angezeigt werden?"
                persistent-hint
                class="mb-4"
              ></v-text-field>

              <v-text-field
                v-model.number="form.maxTotal"
                label="Max. Anzeigen gesamt pro Nutzer"
                type="number"
                min="1"
                outlined
                dense
                hint="Wie oft darf diese Nachricht einem Nutzer insgesamt angezeigt werden?"
                persistent-hint
              ></v-text-field>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Abbrechen</v-btn>
        <v-btn color="primary" @click="save" :disabled="!valid" :loading="saving">
          <v-icon left>mdi-content-save</v-icon>
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PacklistAlertService from '@/services/PacklistAlertService'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

export default {
  name: 'PacklistAlertRuleDialog',
  components: { quillEditor },
  props: {
    value: Boolean,
    rule: Object
  },
  data() {
    return {
      valid: false,
      tab: 0,
      saving: false,
      uploadingImage: false,
      loadingFilterOptions: false,
      form: {
        name: '',
        active: true,
        priority: 'medium',
        filterType: 'OR',
        filterSkus: [],
        filterCountries: [],
        filterCategories: [],
        filterProducts: [],
        messageTitle: '',
        messageContent: '',
        messageBgColor: '#fff3cd',
        messageTextColor: '#856404',
        maxPerDay: 1,
        maxTotal: 3
      },
      priorities: [
        { text: 'Niedrig', value: 'low' },
        { text: 'Mittel', value: 'medium' },
        { text: 'Hoch', value: 'high' },
        { text: 'Kritisch', value: 'critical' }
      ],
      filterOptions: {
        skus: [],
        countries: [],
        categories: [],
        products: []
      },
      skuSearch: null,
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'header': [1, 2, 3, false] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
          ]
        },
        placeholder: 'Nachrichteninhalt hier eingeben...'
      },
      quillInstance: null,
      imageFile: null
    }
  },
  computed: {
    show: {
      get() { return this.value },
      set(val) { this.$emit('input', val) }
    },
    isEdit() {
      return this.rule && this.rule.id
    }
  },
  watch: {
    rule: {
      handler(newVal) {
        if (newVal) {
          this.form = {
            name: newVal.name || '',
            active: newVal.active !== false,
            priority: newVal.priority || 'medium',
            filterType: newVal.filter_type || 'OR',
            filterSkus: newVal.filter_skus || [],
            filterCountries: newVal.filter_countries || [],
            filterCategories: newVal.filter_categories || [],
            filterProducts: newVal.filter_products || [],
            messageTitle: newVal.message_title || '',
            messageContent: newVal.message_content || '',
            messageBgColor: newVal.message_bg_color || '#fff3cd',
            messageTextColor: newVal.message_text_color || '#856404',
            maxPerDay: newVal.max_per_day || 1,
            maxTotal: newVal.max_total || 3
          }
        }
      },
      immediate: true
    }
  },
  created() {
    this.loadFilterOptions()
  },
  methods: {
    onEditorReady(quill) {
      this.quillInstance = quill
    },
    async uploadImage() {
      if (!this.imageFile) return

      const formData = new FormData()
      formData.append('image', this.imageFile)

      this.uploadingImage = true
      try {
        const response = await PacklistAlertService.uploadImage(formData)

        const imageUrl = response.data.image.url

        // Insert image into Quill editor
        if (this.quillInstance) {
          const range = this.quillInstance.getSelection() || { index: 0 }
          this.quillInstance.insertEmbed(range.index, 'image', imageUrl)
          this.$toast?.success('Bild hochgeladen und eingefügt')
        }

        this.imageFile = null
      } catch (error) {
        console.error('Error uploading image:', error)
        this.$toast?.error('Fehler beim Hochladen: ' + (error.response?.data?.error || error.message))
      } finally {
        this.uploadingImage = false
      }
    },
    async loadFilterOptions() {
      this.loadingFilterOptions = true
      try {
        const response = await PacklistAlertService.getFilterOptions()
        this.filterOptions = response.data.options
        console.log('Filter options loaded:', {
          skus: this.filterOptions.skus?.length || 0,
          categories: this.filterOptions.categories?.length || 0,
          products: this.filterOptions.products?.length || 0,
          countries: this.filterOptions.countries?.length || 0
        })
      } catch (error) {
        console.error('Error loading filter options:', error)
        this.$toast?.error('Fehler beim Laden der Filter-Optionen')
      } finally {
        this.loadingFilterOptions = false
      }
    },
    removeFilterItem(field, item) {
      const index = this.form[field].indexOf(item)
      if (index > -1) {
        this.form[field].splice(index, 1)
      }
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$toast?.error('Bitte alle Pflichtfelder ausfüllen')
        return
      }

      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          active: this.form.active,
          priority: this.form.priority,
          filterType: this.form.filterType,
          filterSkus: this.form.filterSkus,
          filterCountries: this.form.filterCountries,
          filterCategories: this.form.filterCategories,
          filterProducts: this.form.filterProducts,
          messageTitle: this.form.messageTitle,
          messageContent: this.form.messageContent,
          messageBgColor: this.form.messageBgColor,
          messageTextColor: this.form.messageTextColor,
          maxPerDay: this.form.maxPerDay,
          maxTotal: this.form.maxTotal
        }

        if (this.isEdit) {
          await PacklistAlertService.updateRule(this.rule.id, payload)
        } else {
          await PacklistAlertService.createRule(payload)
        }

        this.$toast?.success('Regel erfolgreich gespeichert')
        this.$emit('saved')
        this.close()
      } catch (error) {
        console.error('Error saving rule:', error)
        this.$toast?.error('Fehler beim Speichern: ' + (error.response?.data?.error || error.message))
      } finally {
        this.saving = false
      }
    },
    close() {
      this.show = false
      this.resetForm()
      this.tab = 0
    },
    resetForm() {
      this.form = {
        name: '',
        active: true,
        priority: 'medium',
        filterType: 'OR',
        filterSkus: [],
        filterCountries: [],
        filterCategories: [],
        filterProducts: [],
        messageTitle: '',
        messageContent: '',
        messageBgColor: '#fff3cd',
        messageTextColor: '#856404',
        maxPerDay: 1,
        maxTotal: 3
      }
    }
  }
}
</script>

<style scoped>
.v-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
}
</style>

<style>
/* Quill Editor Styling */
.ql-container {
  min-height: 200px;
  font-size: 14px;
}

.ql-editor {
  min-height: 200px;
}
</style>
